/**
 * Sentry error reporting for the Trove web app.
 *
 * Active when `VITE_SENTRY_DSN` is set (see `.env.production` / `.env.example`).
 * Wired from `src/main.tsx` before React renders.
 */
import * as Sentry from '@sentry/react';

type ErrorContext = Record<string, unknown>;

let initialized = false;

function readMetaEnv<T = string | undefined>(key: string): T {
  try {
    const env = (import.meta as unknown as { env?: Record<string, unknown> }).env ?? {};
    return env[key] as T;
  } catch {
    return undefined as T;
  }
}

function readSampleRate(key: string, fallback: number): number {
  const raw = readMetaEnv<string | undefined>(key);
  if (raw == null || raw === '') return fallback;
  const value = Number(raw);
  return Number.isFinite(value) ? value : fallback;
}

function readBooleanEnv(key: string, fallback: boolean): boolean {
  const raw = readMetaEnv<string | undefined>(key);
  if (raw == null || raw === '') return fallback;
  return raw === 'true' || raw === '1';
}

function apiBaseUrl(): string {
  return (
    readMetaEnv<string | undefined>('VITE_API_BASE_URL') ??
    'https://us-central1-trovepile-39142.cloudfunctions.net/api'
  );
}

export function initErrorTracking(): void {
  const dsn = readMetaEnv<string | undefined>('VITE_SENTRY_DSN');
  if (!dsn || typeof dsn !== 'string' || dsn.trim().length === 0) {
    return;
  }

  if (initialized) return;

  const apiOrigin = apiBaseUrl().replace(/\/api\/?$/, '');

  Sentry.init({
    dsn,
    environment:
      readMetaEnv<string | undefined>('VITE_SENTRY_ENVIRONMENT') ?? 'production',
    release: readMetaEnv<string | undefined>('VITE_SENTRY_RELEASE'),
    sendDefaultPii: readBooleanEnv('VITE_SENTRY_SEND_DEFAULT_PII', true),
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    tracesSampleRate: readSampleRate('VITE_SENTRY_TRACES_SAMPLE_RATE', 0.1),
    tracePropagationTargets: [
      'localhost',
      new RegExp(`^${apiOrigin.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`),
      /^https:\/\/app\.trovepile\.com/,
    ],
    replaysSessionSampleRate: readSampleRate(
      'VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE',
      0.1,
    ),
    replaysOnErrorSampleRate: readSampleRate(
      'VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE',
      1.0,
    ),
    enableLogs: readBooleanEnv('VITE_SENTRY_ENABLE_LOGS', true),
  });

  initialized = true;
}

export function captureError(err: unknown, context?: ErrorContext): void {
  if (!initialized) return;
  if (context) {
    Sentry.captureException(err, { extra: context });
  } else {
    Sentry.captureException(err);
  }
}

export function captureMessage(message: string, context?: ErrorContext): void {
  if (!initialized) return;
  if (context) {
    Sentry.captureMessage(message, { extra: context });
  } else {
    Sentry.captureMessage(message);
  }
}

export function setErrorTrackingUser(user: { id?: string; email?: string } | null): void {
  if (!initialized) return;
  Sentry.setUser(user);
}
